/**
 * simulator-description-i18n.ts — Lot 7 i18n (27/05/2026)
 *
 * Helper unique pour résoudre la description d'un simulateur selon la langue
 * active. Évite de répéter la cascade descriptionFr/En/legacy dans chaque
 * composant.
 *
 * Cascade :
 *   - i18n.language === "fr" → descriptionFr || descriptionEn || description || ""
 *   - autre (en, ...)         → descriptionEn || descriptionFr || description || ""
 *
 * Le champ legacy `description` est conservé en dernier recours pour les
 * simulateurs pas encore migrés / pas encore retraduits par le cron.
 */

import type { AgentPresentation } from "../types";

type SimulatorDescriptionFields = {
  description?: string | null;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
};

export function pickSimulatorDescription(
  sim: SimulatorDescriptionFields | null | undefined,
  language: string | undefined,
): string {
  if (!sim) return "";
  const isFr = (language || "fr").toLowerCase().startsWith("fr");
  if (isFr) {
    return sim.descriptionFr || sim.descriptionEn || sim.description || "";
  }
  return sim.descriptionEn || sim.descriptionFr || sim.description || "";
}

// ────────────────────────────────────────────────────────────────────────────
// Lot 8 i18n (28/05/2026) — presentationJson (objectif, compétences, étapes,
// conseils...). Même cascade que la description, mais le champ est du JSON
// sérialisé : on choisit la bonne chaîne brute selon la langue puis on la parse.
// ────────────────────────────────────────────────────────────────────────────

type SimulatorPresentationFields = {
  presentationJson?: string | null;
  presentationJsonFr?: string | null;
  presentationJsonEn?: string | null;
};

/**
 * Retourne la chaîne JSON de présentation brute correspondant à la langue active.
 *
 * Cascade :
 *   - fr → presentationJsonFr || presentationJsonEn || presentationJson || null
 *   - en → presentationJsonEn || presentationJsonFr || presentationJson || null
 */
export function pickSimulatorPresentationRaw(
  sim: SimulatorPresentationFields | null | undefined,
  language: string | undefined,
): string | null {
  if (!sim) return null;
  const isFr = (language || "fr").toLowerCase().startsWith("fr");
  if (isFr) {
    return sim.presentationJsonFr || sim.presentationJsonEn || sim.presentationJson || null;
  }
  return sim.presentationJsonEn || sim.presentationJsonFr || sim.presentationJson || null;
}

/**
 * Retourne le presentationJson parsé (objet) dans la langue active, ou null si
 * absent / JSON invalide. Le composant d'affichage applique son propre fallback
 * (defaultPresentation) sur un retour null.
 */
export function pickSimulatorPresentation<T = unknown>(
  sim: SimulatorPresentationFields | null | undefined,
  language: string | undefined,
): T | null {
  const raw = pickSimulatorPresentationRaw(sim, language);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Normalise un presentationJson parsé (potentiellement partiel) en un AgentPresentation
 * complet, en GARANTISSANT que skills/steps/tips sont toujours des tableaux.
 *
 * Corrige BIZCOACH-R/Q : un simulateur avec un presentationJson incomplet (sans la liste
 * skills/steps/tips) faisait crasher AgentPresentationPage sur `.length` / `.map` d'undefined.
 *
 * @param parsed  l'objet issu de pickSimulatorPresentation (peut être null ou partiel)
 * @param fallbackDescription  description résolue par langue, utilisée si fullDescription absent
 */
export function normalizeAgentPresentation(
  parsed: Partial<AgentPresentation> | null | undefined,
  fallbackDescription = "",
): AgentPresentation {
  const p = parsed ?? {};
  return {
    fullDescription: p.fullDescription || fallbackDescription,
    objective: p.objective ?? "",
    duration: p.duration ?? "",
    skills: Array.isArray(p.skills) ? p.skills : [],
    steps: Array.isArray(p.steps) ? p.steps : [],
    tips: Array.isArray(p.tips) ? p.tips : [],
  };
}
